import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, type ReactNode } from 'react'
import { useNavigate } from 'react-router'

import {
  fetchTourismHospitals,
  fetchTourismSpecialties,
  tourismHospitalsKey,
  tourismSpecialtiesKey,
  type TourismHospitalCard,
} from '../api/tourism'
import { Button } from '../components/Button'
import { EmptyState } from '../components/EmptyState'
import { Footer } from '../components/Footer'
import { Icon } from '../components/Icon'
import { Image } from '../components/Image'
import { Navbar } from '../components/Navbar'
import { SearchInput } from '../components/SearchInput'
import { asset } from '../lib/imageAssets'
import { specialtyIcon } from '../lib/specialtyIcons'

/**
 * Tourism home (M7-T03) — the international medical-tourism landing. Hero +
 * browse-by-specialty grid (real counts from `GET /tourism/specialties`) +
 * partner hospital cards (`GET /tourism/hospitals`), filterable by specialty.
 * No GPS, no maps, no live bed counts.
 *
 * M17-T13 — restyled from `FigmaTourismDesktop` / `FigmaTourismMobile` in
 * `designs/screens-figma-tourism.jsx` (section `Tourism Home`, 710:928).
 *
 * THE ONE THING NOT TO "FIX" HERE. Both surfaces have a concierge entry and
 * they look alike in the frames, so a well-meaning port flattens them. They are
 * different by decision: on Local it is a call CTA and on Tourism it is lead
 * capture, because `TourismEnquiry.tsx` and the admin enquiries queue already
 * back a form here and nothing backs one there. `LocalHome.test.tsx` guards
 * Local against growing an intake; this page's tests guard the routing half.
 *
 * NO EMERGENCY RED ON THIS SURFACE. `--color-sos` marks the emergency journey
 * (ADR-0035). A tourism page borrowing it for visual punch would spend the one
 * signal that must never be ambiguous, so it appears nowhere below and
 * `renders no sos-token element` keeps it that way.
 */
export function TourismHome(): ReactNode {
  const navigate = useNavigate()
  const [specialty, setSpecialty] = useState<string | null>(null)
  const [q, setQ] = useState('')

  const specialties = useQuery({
    queryKey: tourismSpecialtiesKey(),
    queryFn: ({ signal }) => fetchTourismSpecialties(signal),
  })
  const hospitals = useQuery({
    queryKey: tourismHospitalsKey(specialty),
    queryFn: ({ signal }) => fetchTourismHospitals(specialty, signal),
  })

  // PORTED — the hero search bar (713:1642). It narrows what is already on
  // screen rather than calling anything: there is no tourism search endpoint,
  // and inventing one would be a capability change, which ADR-0038 puts outside
  // a visual port. The frame's two-tab segmentation is DROPPED for the same
  // reason — its tabs are "Diagnostics & Pharmacy" and "Specialist", both Local
  // flows, and a tab that cannot lead anywhere is the dead control T05 refused
  // to render.
  const term = q.trim().toLowerCase()
  const shownSpecialties = useMemo(
    () =>
      (specialties.data ?? []).filter((s) =>
        s.name.toLowerCase().includes(term),
      ),
    [specialties.data, term],
  )
  const shownHospitals = useMemo(
    () =>
      (hospitals.data ?? []).filter(
        (h) =>
          h.name.toLowerCase().includes(term) ||
          h.city.toLowerCase().includes(term) ||
          h.specialties.some((s) => s.toLowerCase().includes(term)),
      ),
    [hospitals.data, term],
  )

  return (
    <main className="flex min-h-screen flex-col bg-bg-app font-sans text-ink-1">
      {/* M17-T04 — shared chrome. */}
      <Navbar tagline="International patients" />

      {/* Hero — PORTED from 710:929: centred column, display face over a body
          line on --ink-2, the search bar beneath. The incumbent ran it
          left-aligned in a 2xl column.

          The AMTC eyebrow is INCUMBENT copy and stays. The frame draws no
          eyebrow, but dropping a line that names the commissioning body is a
          content decision and this milestone changes how things look, not what
          they say. */}
      {/* M17-T18c — the AMTC hero photograph sits behind this section. It is
          decorative: the headline beside it already carries the meaning, so
          announcing it would be noise.

          The type went white and gained a scrim, and both were measured rather
          than guessed. The supplied hero is a DIM INDOOR photograph, not the
          bright outdoor image T18c expected, so the risk inverted: the
          incumbent --ink-1 headline scored 1.24–3.58:1 against it and fails AA
          outright. White over a 30% black scrim measures 7.77:1 (390px) and
          7.87:1 (1440px) against the brightest 8px tile behind the text — AAA
          with margin, and the photograph still reads through it. */}
      <section className="relative isolate flex flex-col items-center gap-6 overflow-hidden px-5 py-12 sm:px-8 sm:py-16">
        <Image
          asset={asset('tourism/hero')}
          decorative
          eager
          className="absolute inset-0 -z-20 h-full w-full"
        />
        <div aria-hidden="true" className="absolute inset-0 -z-10 bg-black/30" />

        <div className="flex max-w-[660px] flex-col items-center gap-4 text-center">
          <span className="font-mono text-[11px] uppercase tracking-wide text-white/80">
            AMTC · Africa Medical Tourism Council
          </span>
          <h1 className="font-display text-d-md font-medium tracking-display text-white sm:text-d-lg">
            World-class care, in Ghana.
          </h1>
          <p className="max-w-lg text-[16px] leading-6 text-white/90 sm:text-[18px] sm:leading-7">
            Ten accredited partner hospitals in Accra serving international
            patients across fifteen specialties. Browse, compare, and send your
            case to the AMTC concierge — they take it from there.
          </p>
        </div>
        <div className="w-full max-w-[542px]">
          <SearchInput
            value={q}
            onChange={setQ}
            label="Search treatments or hospitals"
            placeholder="Search treatments or hospitals"
          />
        </div>
      </section>

      {/* NO "PROUD PARTNERS" LOGO BAND. The frame draws one (710:1269 and the
          desktop rows 718:2334 / 719:2357) and it is deliberately not ported
          here: four of the ten partners have no upstream asset at all (audit
          gap G2), and the frame's own rows carry a duplicate and a non-partner
          (G3, G1). Rendering it from the names we do have would just repeat the
          cards below. Imagery is M17-T18's ticket, and the band belongs to it. */}

      {/* Browse by specialty */}
      <section aria-labelledby="specialties-h" className="px-5 py-8 sm:px-8">
        <div className="mb-4 flex flex-wrap items-baseline justify-between gap-2">
          {/* PORTED — FgSectionTitle: display face at --fs-d-xs, Medium,
              --ink-1. The incumbent was text-xl semibold in the body face. */}
          <h2
            id="specialties-h"
            className="font-display text-d-xs font-medium tracking-display text-ink-1"
          >
            Browse by speciality
          </h2>
          <span className="text-[14px] leading-5 text-ink-3">
            15 specialties · 10 partner hospitals
          </span>
        </div>

        {specialties.isPending ? (
          <div
            role="status"
            aria-label="Loading specialties"
            className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
          >
            {Array.from({ length: 5 }, (_, i) => (
              <div
                key={i}
                className="h-[84px] animate-pulse rounded-r3 bg-bg-sunken motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : specialties.isError ? (
          <div role="alert">
            <EmptyState
              title="Couldn't load specialties"
              body="Check your connection and try again."
              headingLevel="h3"
              action={
                <Button
                  kind="secondary"
                  onClick={() => void specialties.refetch()}
                >
                  Try again
                </Button>
              }
            />
          </div>
        ) : shownSpecialties.length === 0 ? (
          <EmptyState
            title="No specialties match that search"
            body="Try a broader term, or talk to the AMTC concierge."
            headingLevel="h3"
            action={
              <Button kind="secondary" onClick={() => setQ('')}>
                Clear search
              </Button>
            }
          />
        ) : (
          <div
            role="group"
            aria-label="Specialty"
            className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5"
          >
            {term === '' && (
              <SpecialtyTile
                label="All specialties"
                count={null}
                active={specialty === null}
                onClick={() => setSpecialty(null)}
              />
            )}
            {shownSpecialties.map((s) => (
              <SpecialtyTile
                key={s.slug}
                slug={s.slug}
                label={s.name}
                count={s.hospital_count}
                active={specialty === s.slug}
                onClick={() => setSpecialty(s.slug)}
              />
            ))}
          </div>
        )}
      </section>

      {/* Partner hospitals. The heading keeps its incumbent words — the frame
          heads this "6 Partner Hospitals based on selection" over six cards for
          three hospitals (G30), so its count is not a thing to copy. */}
      <section aria-labelledby="hospitals-h" className="px-5 pb-10 sm:px-8">
        <h2
          id="hospitals-h"
          className="mb-4 font-display text-d-xs font-medium tracking-display text-ink-1"
        >
          Partner hospitals
        </h2>
        {hospitals.isPending ? (
          <div
            role="status"
            aria-label="Loading hospitals"
            className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3"
          >
            {Array.from({ length: 3 }, (_, i) => (
              <div
                key={i}
                className="h-[188px] animate-pulse rounded-r3 bg-bg-sunken motion-reduce:animate-none"
              />
            ))}
          </div>
        ) : hospitals.isError ? (
          <div role="alert">
            <EmptyState
              title="Couldn't load hospitals"
              body="Check your connection and try again."
              headingLevel="h3"
              action={
                <Button
                  kind="secondary"
                  onClick={() => void hospitals.refetch()}
                >
                  Try again
                </Button>
              }
            />
          </div>
        ) : shownHospitals.length === 0 ? (
          <EmptyState
            title="No partner hospitals match"
            body="Talk to the AMTC concierge and they will find one."
            headingLevel="h3"
            action={
              <Button
                kind="secondary"
                onClick={() => navigate('/tourism/enquiry')}
              >
                Talk to AMTC
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {shownHospitals.map((h) => (
              <HospitalCard
                key={h.slug}
                hospital={h}
                onOpen={() => navigate(`/tourism/hospital/${h.slug}`)}
              />
            ))}
          </div>
        )}
      </section>

      <HowItWorks />

      <ConciergeIntake
        onContinue={(condition) =>
          navigate(
            condition
              ? `/tourism/enquiry?condition=${encodeURIComponent(condition)}`
              : '/tourism/enquiry',
          )
        }
      />

      <Footer />
    </main>
  )
}

/** PORTED — FG_TOURISM_STEPS. M16 reworded these off deferred capabilities
 *  (nothing here promises a booking), which is why the copy is adopted as
 *  written rather than re-derived. */
const STEPS: ReadonlyArray<{ n: string; title: string; body: string }> = [
  {
    n: '1',
    title: 'Tell us what you need',
    body: 'Share your condition and preferred dates with the AMTC concierge.',
  },
  {
    n: '2',
    title: 'Get matched',
    body: 'AMTC picks the partner hospital and specialist that fit your case.',
  },
  {
    n: '3',
    title: 'Plan the trip',
    body: 'Treatment plan, indicative costs and travel guidance, in one reply.',
  },
  {
    n: '4',
    title: 'Arrive prepared',
    body: 'The hospital has your details before you land.',
  },
]

function HowItWorks(): ReactNode {
  return (
    <section aria-labelledby="how-h" className="px-5 pb-10 sm:px-8">
      <h2
        id="how-h"
        className="mb-6 font-display text-d-xs font-medium tracking-display text-ink-1"
      >
        How it works
      </h2>
      <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
        {STEPS.map((s) => (
          <div key={s.n} className="flex flex-col gap-2">
            <span
              // Decorative: the step order is already carried by the reading
              // order and by the title, so the numeral is not announced twice.
              aria-hidden="true"
              className="grid h-8 w-8 place-items-center rounded-full bg-accent text-[14px] font-medium text-accent-on"
            >
              {s.n}
            </span>
            <span className="text-[18px] font-medium leading-7 text-ink-1">
              {s.title}
            </span>
            <span className="text-[16px] leading-6 text-ink-3">{s.body}</span>
          </div>
        ))}
      </div>
    </section>
  )
}

/**
 * PORTED — `FgConciergeIntake`. The frame draws ONE box, mapping to
 * `condition_description`, which is optional in `EnquiryCreate`. The four
 * fields the schema requires — `full_name`, `email`, `country_of_origin` and
 * `consent_data_handling` — are drawn nowhere upstream, so a one-box intake
 * cannot create an enquiry. It is therefore an entry point that carries the
 * condition into the full form, not a form that submits on its own.
 *
 * The frame promises a reply "within 24 hours"; the shipped copy says "within
 * one business day" and the shipped SLA wins.
 */
function ConciergeIntake({
  onContinue,
}: {
  onContinue: (condition: string) => void
}): ReactNode {
  const [condition, setCondition] = useState('')

  return (
    <section className="px-5 pb-12 sm:px-8">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          onContinue(condition.trim())
        }}
        aria-labelledby="concierge-h"
        className="flex flex-col items-center gap-3 rounded-r3 bg-accent-bg px-6 py-8 text-center sm:px-12 sm:py-12"
      >
        <h2
          id="concierge-h"
          className="font-display text-d-xs font-medium tracking-display text-ink-1"
        >
          Talk to the AMTC concierge
        </h2>
        <p className="max-w-[520px] text-[16px] leading-6 text-ink-2">
          Send your case summary and receive matched hospital recommendations,
          cost estimates and travel guidance — within one business day.
        </p>
        <div className="flex w-full max-w-[520px] flex-col gap-3 text-left">
          <label
            htmlFor="concierge-condition"
            className="text-[16px] font-medium leading-6 text-ink-1"
          >
            What is your current medical condition?
          </label>
          <input
            id="concierge-condition"
            type="text"
            value={condition}
            onChange={(e) => setCondition(e.target.value)}
            placeholder="e.g. knee replacement, second opinion on a scan"
            className="w-full min-w-0 rounded-r3 border border-line-1 bg-bg-app px-[21px] py-[17px] text-[16px] leading-6 text-ink-1 outline-none placeholder:text-ink-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
          />
          <div className="flex justify-end">
            <Button type="submit">Continue to enquiry</Button>
          </div>
        </div>
      </form>
    </section>
  )
}

function SpecialtyTile({
  slug,
  label,
  count,
  active,
  onClick,
}: {
  /** Omitted by the "All specialties" tile, which has no icon of its own. */
  slug?: string
  label: string
  count: number | null
  active: boolean
  onClick: () => void
}): ReactNode {
  const icon = specialtyIcon(slug)
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      // PORTED — FgTourismSpecialtyGrid: --bg-app on a --line-1 border at
      // --r-3, a 20px badge glyph in --accent-ink, the name at --fs-md and the
      // count at --fs-sm on --ink-3. The incumbent was a 13px/11px pair at
      // rounded-[10px], a literal that was only correct while --radius-r3 was
      // 10px.
      //
      // DERIVED — the selected state. The frame draws no active tile (G31);
      // `aria-pressed` carries it for AT and the accent border/fill carries it
      // visually, so colour is not the only signal.
      className={
        active
          ? 'flex flex-col items-start gap-1 rounded-r3 border border-accent bg-accent-bg p-4 text-left'
          : 'flex flex-col items-start gap-1 rounded-r3 border border-line-1 bg-bg-app p-4 text-left hover:bg-bg-soft focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent'
      }
    >
      {/* M17-T18c — the AMTC specialty icon replaces the generic badge glyph
          where one exists. Decorative: the specialty name is the next line and
          is what assistive tech announces, so an icon saying "Cardiology"
          again would be a duplicate. "All specialties" has no icon and keeps
          the badge, as does any slug the backend grows without one. */}
      {icon ? (
        <Image asset={icon} decorative className="h-5 w-5" />
      ) : (
        <span className="text-accent-ink" aria-hidden="true">
          <Icon name="badge" size={20} />
        </span>
      )}
      <span className="text-[16px] leading-6 text-ink-1">{label}</span>
      <span className="text-[14px] leading-5 text-ink-3">
        {/* The frame writes "2 partner hospital" and "1 partner hosptial"
            (G15); the grammar is fixed here, as it was in designs/. */}
        {count == null
          ? 'Show all'
          : `${count} partner ${count === 1 ? 'hospital' : 'hospitals'}`}
      </span>
    </button>
  )
}

/**
 * The tourism hospital card. Deliberately NOT the shared `HospitalCard`: that
 * one is built around bed badges and a live-count row, and this surface shows
 * neither. The frame's tourism card carries specialties in that slot, and
 * ADR-0027 gates bed price behind a bed-type choice Tourism never makes — so
 * there is no nightly rate here, and no "Average Waitime" either (G24, G25).
 * What is shared is the language: the same --r-3 shell, --shadow-card, hover
 * lift, 18/28 Medium name and 14/20 --ink-2 meta line.
 */
function HospitalCard({
  hospital,
  onOpen,
}: {
  hospital: TourismHospitalCard
  onOpen: () => void
}): ReactNode {
  return (
    <article className="flex flex-col gap-3 rounded-r3 border border-line-1 bg-bg-app p-4 shadow-card transition-[box-shadow,transform] hover:-translate-y-0.5 hover:border-line-2 hover:shadow-lift">
      <div className="flex items-start gap-3">
        {/* The media well stands empty until M17-T18 lands the AMTC
            photography. It is a shaped placeholder rather than a grey box so
            the card reads at its final proportions now. */}
        <div
          className="grid h-[88px] w-[88px] shrink-0 place-items-center overflow-hidden rounded-r3 bg-gradient-to-br from-bg-soft to-bg-sunken text-ink-4"
          aria-hidden="true"
        >
          <Icon name="bed" size={26} />
        </div>
        <div className="min-w-0">
          <h3 className="text-[18px] font-medium leading-7 text-ink-1">
            {hospital.name}
          </h3>
          <p className="mt-0.5 flex items-center gap-1 text-[14px] leading-5 text-ink-2">
            <Icon name="pin" size={14} /> {hospital.city}
          </p>
        </div>
      </div>

      {hospital.accreditations.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {hospital.accreditations.map((a) => (
            <span
              key={a}
              className="inline-flex items-center gap-1 rounded-full bg-accent-bg px-2 py-0.5 text-[12px] font-medium text-accent-ink"
            >
              <Icon name="badge" size={12} /> {a}
            </span>
          ))}
        </div>
      )}

      {hospital.specialties.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {hospital.specialties.slice(0, 4).map((s) => (
            <span
              key={s}
              className="rounded-full border border-line-1 bg-bg-soft px-2 py-0.5 text-[12px] text-ink-2"
            >
              {s}
            </span>
          ))}
          {hospital.specialties.length > 4 && (
            <span className="px-1 text-[12px] text-ink-3">
              +{hospital.specialties.length - 4} more
            </span>
          )}
        </div>
      )}

      <p className="text-[14px] leading-5 text-ink-3">
        Languages: {hospital.languages.join(', ')}
      </p>

      <Button className="mt-auto self-start" onClick={onOpen}>
        View hospital
      </Button>
    </article>
  )
}
